/**
 * # Hero carousel — Alpine data factory
 *
 * The homepage opener (`modcarousel_main`): a **single full-stage slide at a
 * time**, not a rail. Every slide is stacked in one CSS-grid cell, so the stage
 * auto-sizes to the **tallest** slide and the nav arrows — pinned to the stable
 * white panel — never move regardless of copy length. Advancing slides the
 * active panel out of the viewport on the X axis while the next enters from the
 * opposite edge; the full-bleed background cross-fades independently (driven by
 * the same `current` index, in the markup).
 *
 * ## Why this is layout-free JS
 * The component writes a single custom property — `--ci`, the current index — on
 * its root. The stacked content panels position themselves entirely in CSS:
 * `translateX(calc((var(--i) - var(--ci)) * 100%))`, where each panel carries its
 * own static `--i`. So the active panel sits at 0, the previous ones at −100%,
 * the next ones at +100% — the slide direction emerges from the index delta with
 * no per-slide JS writes, and a CSS transition on `transform` animates it. The
 * background layers read `current` directly via Alpine `:class` (cumulative
 * opacity: a layer is opaque while `current >= its index`, which gives a
 * dip-free cross-fade in both directions and stays visually static when adjacent
 * slides share the same background image).
 *
 * Unlike the scroll-snap {@link carousel} (native scroll) and the
 * {@link featuredCarousel} rail (widths morph), this is a stacked stage — hence
 * its own minimal factory. The last reachable index is `count - 1` (one slide on
 * screen), where {@link featuredCarousel} stops at `count - 2`.
 *
 * ## Touch swipe
 * The stage is transform-positioned (no native scroll), so horizontal swiping is
 * wired explicitly: a **touch** drag past {@link SWIPE_THRESHOLD} advances one
 * step (left → `next`, right → `prev`). The root carries `touch-action: pan-y`
 * so vertical drags scroll the page (the browser then fires `pointercancel` and
 * the swipe aborts). Mouse/pen pointers are ignored so CTA clicks are never
 * swallowed — desktop uses the arrows.
 *
 * ## Markup contract
 * - Root: `x-data="heroCarousel"`, `touch-pan-y`. Receives `--ci`.
 * - Each content panel: `data-slide` + an inline `--i` (its index); reads `--ci`.
 * - Background layers: bound with `:class` against `current` (no `data-slide`).
 *
 * ## Exposed state / API (shared arrow contract with `item_gallery_nav`)
 * - `current` — active slide index
 * - `count` — number of slides
 * - `atStart` / `atEnd` — booleans for disabling the prev/next arrows
 * - `prev()` / `next()` / `goTo(i)` — step / jump
 * - `isActive(i)` — `current === i`
 */

/** Minimum horizontal touch travel (px) that counts as a swipe, not a tap. */
const SWIPE_THRESHOLD = 40;

export interface HeroCarouselComponent {
  current: number;
  count: number;
  atStart: boolean;
  atEnd: boolean;
  _root: HTMLElement | null;
  _startX: number | null;
  _onDown: (e: PointerEvent) => void;
  _onUp: (e: PointerEvent) => void;
  _onCancel: () => void;
  init(): void;
  destroy(): void;
  _maxIndex(): number;
  _apply(): void;
  goTo(index: number): void;
  next(): void;
  prev(): void;
  isActive(index: number): boolean;
}

export function heroCarousel(): HeroCarouselComponent {
  return {
    current: 0,
    count: 0,
    atStart: true,
    atEnd: false,

    _root: null,
    _startX: null,
    _onDown: () => {},
    _onUp: () => {},
    _onCancel: () => {},

    init(): void {
      const root = (this as unknown as { $el: HTMLElement }).$el;
      this._root = root;
      this.count = root.querySelectorAll('[data-slide]').length;
      this._apply();

      // Touch swipe — only touch pointers, so mouse/pen CTA clicks pass through.
      this._onDown = (e: PointerEvent): void => {
        if (e.pointerType !== 'touch') {
          return;
        }
        this._startX = e.clientX;
        root.setPointerCapture(e.pointerId);
      };
      this._onUp = (e: PointerEvent): void => {
        if (this._startX === null) {
          return;
        }
        const dx = e.clientX - this._startX;
        this._startX = null;
        if (Math.abs(dx) >= SWIPE_THRESHOLD) {
          if (dx < 0) {
            this.next();
          } else {
            this.prev();
          }
        }
      };
      this._onCancel = (): void => {
        this._startX = null;
      };
      root.addEventListener('pointerdown', this._onDown);
      root.addEventListener('pointerup', this._onUp);
      root.addEventListener('pointercancel', this._onCancel);
    },

    destroy(): void {
      this._root?.removeEventListener('pointerdown', this._onDown);
      this._root?.removeEventListener('pointerup', this._onUp);
      this._root?.removeEventListener('pointercancel', this._onCancel);
    },

    // One slide on screen, so the last reachable index is the last slide.
    _maxIndex(): number {
      return Math.max(0, this.count - 1);
    },

    _apply(): void {
      const max = this._maxIndex();
      this.current = Math.max(0, Math.min(this.current, max));
      this._root?.style.setProperty('--ci', String(this.current));
      this.atStart = this.current <= 0;
      this.atEnd = this.current >= max;
    },

    goTo(index: number): void {
      this.current = index;
      this._apply();
    },

    next(): void {
      this.goTo(this.current + 1);
    },

    prev(): void {
      this.goTo(this.current - 1);
    },

    isActive(index: number): boolean {
      return this.current === index;
    },
  };
}
